import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, BookOpen, User, Tag, ShoppingCart, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function BookDetail() {
  const { pageId, bookIndex, slug, bookSlug } = useParams();
  const content = useSiteContent();
  const [lightbox, setLightbox] = useState(null);

  if (!content) return <div className="min-h-screen flex items-center justify-center bg-[#F4F7F6]"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  // Find page: by pageId directly, or by slug from menu_config
  let actualPageId = pageId;
  const mc = content.menu_config;
  if (!actualPageId && slug && mc?.items) {
    const mi = mc.items.find(m => m.slug === slug);
    if (mi) actualPageId = mi.id;
  }
  let pg = content[actualPageId];
  if (!pg && slug) {
    // Try slug as pageId
    if (mc?.items) {
      const mi = mc.items.find(m => m.slug === slug || m.id === slug);
      if (mi) pg = content[mi.id];
    }
  }
  if (!pg) return <div className="min-h-screen flex items-center justify-center"><p className="text-[#5C6B7A]">Səhifə tapılmadı</p></div>;

  const bookSection = (pg.sections || []).find(s => s.type === 'books');
  const books = bookSection?.items || [];
  
  // Find book: by bookIndex (number) or by bookSlug (string)
  let idx;
  const bSlug = bookSlug || bookIndex;
  if (bSlug && isNaN(bSlug)) {
    idx = books.findIndex(b => b.slug === bSlug);
    if (idx === -1) idx = 0;
  } else {
    idx = parseInt(bSlug) || 0;
  }
  const book = books[idx];
  if (!book) return <div className="min-h-screen flex items-center justify-center"><p className="text-[#5C6B7A]">Kitab tapılmadı</p></div>;

  const parentSlug = mc?.items?.find(m => content[m.id] === pg)?.slug || pageId || slug || 'publications';
  const parentUrl = `/${parentSlug}`;
  const parentTitle = pg.hero_title || 'Nəşrlərimiz';
  const gallery = book.gallery || [];
  const statusMap = { coming: { label: 'Tezliklə', color: 'bg-amber-500' }, available: { label: 'Mövcuddur', color: 'bg-green-500' }, new: { label: 'Yeni nəşr', color: 'bg-[#0077B6]' } };
  const otherBooks = books.filter((_, i) => i !== idx).slice(0, 3);

  return (
    <div className="bg-[#F4F7F6] min-h-screen">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-[#03045E] via-[#0077B6] to-[#00B4D8]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-20 w-32 h-32 rounded-full border-2 border-white/30" />
          <div className="absolute bottom-10 right-20 w-48 h-48 rounded-full border-2 border-white/20" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <Link to={parentUrl} className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {parentTitle}
          </Link>
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            {/* Book Cover */}
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} className="flex-shrink-0">
              {book.image ? (
                <img src={book.image} alt={book.title} className="w-56 md:w-64 h-auto rounded-2xl shadow-2xl border-4 border-white/20" />
              ) : (
                <div className="w-56 md:w-64 h-80 bg-white/10 rounded-2xl flex items-center justify-center border-2 border-white/20">
                  <BookOpen className="w-20 h-20 text-white/30" />
                </div>
              )}
            </motion.div>
            {/* Book Info */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="flex-1 text-center lg:text-left">
              {book.status && (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg mb-4 ${statusMap[book.status]?.color || 'bg-slate-500'}`}>
                  {statusMap[book.status]?.label || book.status}
                </span>
              )}
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">{book.title}</h1>
              {book.author && (
                <p className="flex items-center gap-2 text-white/80 text-lg mb-4 justify-center lg:justify-start">
                  <User className="w-5 h-5" /> {book.author}
                </p>
              )}
              {book.price && (
                <p className="text-3xl font-bold text-[#48CAE4] mb-6">{book.price}</p>
              )}
              {book.url && (
                <a href={book.url} target={book.url.startsWith('http') ? '_blank' : '_self'} rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white text-[#0077B6] font-bold hover:shadow-lg transition-all group">
                  <ShoppingCart className="w-5 h-5" /> Sifariş üçün <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl font-bold text-[#1A2530] mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#0077B6]" /> Kitab haqqında
            </h2>
            {book.desc && (
              <div className="[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-[#1A2530] [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#0077B6] [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:text-[#5C6B7A] [&_p]:leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-[#5C6B7A] [&_ol]:list-decimal [&_ol]:pl-6 [&_strong]:text-[#1A2530]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{book.desc}</ReactMarkdown>
              </div>
            )}
          </motion.div>

          {/* Gallery */}
          {gallery.length > 0 && (
            <div className="mt-10">
              <h2 className="text-2xl font-bold text-[#1A2530] mb-6">Kitabdan nümunələr</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {gallery.map((img, gi) => (
                  <button key={gi} onClick={() => setLightbox(gi)} className="rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-zoom-in">
                    <img src={img} alt={`Nümunə ${gi+1}`} className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Other Books */}
      {otherBooks.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl font-bold text-[#1A2530] mb-8">Digər Nəşrlər</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherBooks.map((ob, oi) => {
                const origIdx = books.indexOf(ob);
                return (
                  <Link key={oi} to={`/${parentSlug}/${ob.slug || origIdx}`} className="group bg-[#F4F7F6] rounded-2xl overflow-hidden hover:shadow-lg transition-all">
                    <div className="h-48 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                      {ob.image ? <img src={ob.image} alt="" className="h-full w-auto object-contain group-hover:scale-105 transition-transform" />
                      : <BookOpen className="w-12 h-12 text-slate-300" />}
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-[#1A2530] group-hover:text-[#0077B6] transition-colors">{ob.title}</h3>
                      {ob.author && <p className="text-sm text-[#0077B6] mt-1">{ob.author}</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && gallery.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] bg-black/90 flex items-center justify-center" onClick={() => setLightbox(null)}>
            <button onClick={e => { e.stopPropagation(); setLightbox(null); }} className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center z-10"><X className="w-6 h-6 text-white" /></button>
            {gallery.length > 1 && (<>
              <button onClick={e => { e.stopPropagation(); setLightbox((lightbox-1+gallery.length)%gallery.length); }} className="absolute left-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center z-10"><ChevronLeft className="w-6 h-6 text-white" /></button>
              <button onClick={e => { e.stopPropagation(); setLightbox((lightbox+1)%gallery.length); }} className="absolute right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center z-10"><ChevronRight className="w-6 h-6 text-white" /></button>
            </>)}
            <motion.img key={lightbox} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} src={gallery[lightbox]} alt="" className="max-w-[90vw] max-h-[85vh] object-contain rounded-2xl" onClick={e => e.stopPropagation()} />
            <div className="absolute bottom-6 text-white/60 text-sm">{lightbox+1} / {gallery.length}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
